/**
 * Game over (UX-SPEC §4): an overlay card with the result, the reason, final
 * scores with the end-game rack adjustments, highlights, both revealed racks,
 * the series score and Rematch / Back to lobby. Dismissing it reveals the
 * final board; a slim bar in place of the action bar brings it back.
 */
import { useRef, useState, type RefObject } from 'react';
import clsx from 'clsx';
import type { GameResult, GameView, MoveRecord, RoomView, Seat, Tile } from '@wordloom/engine';
import { voteRematch } from '../game/actions';
import { opponentSeat, seatName } from '../game/events';
import { formatScore, formatScorePair } from '../lib/format';
import { leave } from '../net/session';
import { useUiStore } from '../state/uiStore';
import { AnimatedNumber } from './AnimatedNumber';
import { IconCrown, IconDoor, IconRefresh, IconScales, IconSparkle } from './Icons';
import { Modal } from './Modal';
import { MiniTile } from './Tile';
import { Tooltip } from './Tooltip';
import './GameOver.css';

// ---------------------------------------------------------------------------
// Pure helpers (unit-tested)
// ---------------------------------------------------------------------------

const NUMBER_WORDS = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];

/** "You win!" / "Alex wins" / "Draw". */
export function resultTitle(result: GameResult, you: Seat, names: readonly [string, string]): string {
  if (result.winner === null) return 'Draw';
  return result.winner === you ? 'You win!' : `${names[result.winner]} wins`;
}

/** "Maya went out" / "Four passes in a row" / "Alex resigned" / "Win by forfeit". */
export function resultReason(result: GameResult, you: Seat, names: readonly [string, string], passLimit: number): string {
  const who = (seat: Seat | null) => (seat === null ? 'Someone' : seat === you ? 'You' : names[seat]);
  switch (result.reason) {
    case 'out':
      return `${who(result.endedBy)} went out`;
    case 'passes':
      return `${NUMBER_WORDS[passLimit] ?? passLimit} passes in a row`;
    case 'resign':
      return `${who(result.endedBy)} resigned`;
    case 'forfeit':
      return result.winner === you ? 'Win by forfeit' : `${who(result.endedBy)} forfeited`;
  }
}

/**
 * How the end-game rack adjustment turns the score from play into the final
 * score, so the card reconciles with the history's last row:
 * "531 + 12 from Alex’s rack = 543" / "12 − 22 for tiles left = −10" / null.
 * The score from play is `final − adjustment` (finalScores already include it).
 */
export function adjustmentNote(
  result: GameResult,
  seat: Seat,
  you: Seat,
  names: readonly [string, string],
): string | null {
  const adj = result.adjustments[seat];
  if (!adj) return null;
  const final = result.finalScores[seat];
  const base = final - adj;
  if (adj > 0) {
    const other: Seat = seat === 0 ? 1 : 0;
    return `${formatScore(base)} + ${adj} from ${other === you ? 'your' : `${names[other]}’s`} rack = ${formatScore(final)}`;
  }
  return `${formatScore(base)} − ${Math.abs(adj)} for tiles left = ${formatScore(final)}`;
}

/**
 * The final score in the order the title reads it: the winner's first
 * ("Ben wins 16–10", never "Ben wins 10–16" on the loser's screen), yours
 * first on a draw.
 */
export function resultScoreLine(result: GameResult, you: Seat): string {
  const first: Seat = result.winner ?? you;
  const second: Seat = first === 0 ? 1 : 0;
  return formatScorePair(result.finalScores[first], result.finalScores[second]);
}

export interface Highlights {
  /** The highest-scoring single word of the game. */
  best: { word: string; score: number; seat: Seat } | null;
  bingos: [number, number];
  /** Plays made (moves that put words on the board). */
  wordsPlayed: number;
}

export function gameHighlights(history: readonly MoveRecord[]): Highlights {
  let best: Highlights['best'] = null;
  const bingos: [number, number] = [0, 0];
  let wordsPlayed = 0;
  for (const m of history) {
    if (m.type !== 'play') continue;
    wordsPlayed++;
    if (m.bingo) bingos[m.seat]++;
    for (const w of m.words) {
      if (!best || w.score > best.score) best = { word: w.word, score: w.score, seat: m.seat };
    }
  }
  return { best, bingos, wordsPlayed };
}

// ---------------------------------------------------------------------------
// Rematch state
// ---------------------------------------------------------------------------

function useRematch(view: RoomView) {
  const [busy, setBusy] = useState(false);
  const opp = opponentSeat(view);
  const oppName = seatName(view, opp);
  const iVoted = view.rematchVotes.includes(view.you);
  const theyVoted = view.rematchVotes.includes(opp);
  const oppPresent = view.seats[opp]?.connected ?? false;
  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    await voteRematch(!iVoted);
    setBusy(false);
  };
  const label = iVoted ? `Waiting for ${oppName}…` : theyVoted ? 'Accept rematch' : 'Rematch';
  const tip = iVoted
    ? 'Withdraw your rematch request'
    : theyVoted
      ? `Start a new game with ${oppName} now`
      : `Ask ${oppName} for another game — it starts when you both agree`;
  return { busy, iVoted, theyVoted, oppName, oppPresent, toggle, label, tip };
}

function RematchButton({ view, buttonRef }: { view: RoomView; buttonRef?: RefObject<HTMLButtonElement | null> }) {
  const r = useRematch(view);
  return (
    <Tooltip content={r.tip}>
      <button
        ref={buttonRef}
        type="button"
        className={clsx('btn gameover__rematch', r.iVoted ? 'btn--ghost rematch--waiting' : 'btn--primary')}
        aria-pressed={r.iVoted}
        aria-disabled={r.busy || undefined}
        onClick={() => void r.toggle()}
      >
        {r.busy || r.iVoted ? <span className="btn__spinner" aria-hidden="true" /> : <IconRefresh />}
        <span className="gameover__rematch-label">{r.label}</span>
      </button>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Overlay
// ---------------------------------------------------------------------------

function RackReveal({ tiles }: { tiles: readonly Tile[] }) {
  if (tiles.length === 0) return <span className="gameover__out">Went out</span>;
  return (
    <span className="gameover__rack">
      {/* Real text, not an aria-label: a role-less span may not carry one. The tiles' own glyphs are aria-hidden. */}
      <span className="sr-only">Tiles left: {tiles.map((t) => (t.blank ? 'blank' : t.letter)).join(', ')}</span>
      {tiles.map((t) => (
        <MiniTile key={t.id} letter={t.blank ? '' : t.letter} value={t.value} blank={t.blank} className="gameover__tile" />
      ))}
    </span>
  );
}

export function GameOverDialog({ view, game }: { view: RoomView; game: GameView }) {
  const dismissed = useUiStore((s) => s.gameOverDismissed);
  const setDismissed = useUiStore((s) => s.setGameOverDismissed);
  const rematchRef = useRef<HTMLButtonElement>(null);
  const rematch = useRematch(view);
  const result = game.result;
  if (!result) return null;

  const you = view.you;
  const opp = opponentSeat(view);
  const names: [string, string] = [seatName(view, 0), seatName(view, 1)];
  const title = resultTitle(result, you, names);
  const reason = resultReason(result, you, names, game.passLimit);
  const won = result.winner === you;
  const draw = result.winner === null;
  const h = gameHighlights(game.history);
  const wins: [number, number] = [view.seats[0]?.wins ?? 0, view.seats[1]?.wins ?? 0];
  const order: Seat[] = [you, opp];

  return (
    <Modal
      open={!dismissed}
      onClose={() => setDismissed(true)}
      title={`${title} ${reason}.`}
      hideTitle
      size="md"
      className={clsx('gameover', won && 'gameover--won', draw && 'gameover--draw')}
      initialFocus={rematchRef}
      footer={
        <>
          <Tooltip content="Leave the room — it stays in the lobby’s Resume list">
            <button type="button" className="btn btn--ghost gameover__lobby" onClick={() => leave({ forget: false })}>
              <IconDoor /> Back to lobby
            </button>
          </Tooltip>
          <Tooltip content="Hide this card to study the final board">
            <button type="button" className="btn gameover__view" onClick={() => setDismissed(true)}>
              View board
            </button>
          </Tooltip>
          <RematchButton view={view} buttonRef={rematchRef} />
        </>
      }
    >
      <div className="gameover__hero" aria-hidden="true">
        <span className="gameover__crown">{draw ? <IconScales /> : <IconCrown />}</span>
        <p className="gameover__title">{title}</p>
        <p className="gameover__reason">{reason}</p>
      </div>

      <ol className="gameover__scores" aria-label="Final scores">
        {order.map((seat) => {
          const note = adjustmentNote(result, seat, you, names);
          const isWinner = result.winner === seat;
          return (
            <li key={seat} className={clsx('gameover__row', isWinner && 'is-winner', `gameover__row--seat${seat}`)}>
              <span className="gameover__avatar" aria-hidden="true">
                {(names[seat].trim()[0] ?? '?').toUpperCase()}
              </span>
              <span className="gameover__who">
                <span className="gameover__name" title={names[seat]}>
                  <span className="gameover__name-text">{seat === you ? `${names[seat]} (you)` : names[seat]}</span>
                  {isWinner && <IconCrown className="gameover__mini-crown" aria-hidden="true" />}
                </span>
                {note && <span className="gameover__adj">{note}</span>}
                <RackReveal tiles={result.remainingRacks[seat]} />
              </span>
              <span className="gameover__score tabular">
                <span className="sr-only">{names[seat]}: </span>
                <AnimatedNumber value={result.finalScores[seat]} />
              </span>
            </li>
          );
        })}
      </ol>

      <dl className="gameover__stats">
        <div>
          <dt>Best word</dt>
          <dd>
            {h.best ? (
              <>
                <span className="gameover__word">{h.best.word}</span> {h.best.score}
                <span className="gameover__by">by {h.best.seat === you ? 'you' : names[h.best.seat]}</span>
              </>
            ) : (
              '—'
            )}
          </dd>
        </div>
        <div>
          <dt>Bingos</dt>
          <dd>
            {h.bingos[you]}–{h.bingos[opp]}
            {h.bingos[0] + h.bingos[1] > 0 && <IconSparkle className="gameover__spark" aria-hidden="true" />}
          </dd>
        </div>
        <div>
          <dt>Words played</dt>
          <dd>{h.wordsPlayed}</dd>
        </div>
        <div>
          <dt>Series</dt>
          <dd>
            {wins[you]}–{wins[opp]}
          </dd>
        </div>
      </dl>

      {(rematch.theyVoted && !rematch.iVoted) || !rematch.oppPresent ? (
        // Not a live region: the app-wide announcer already speaks rematch events.
        <p className={clsx('gameover__note', rematch.theyVoted && !rematch.iVoted && 'is-invite')}>
          {rematch.theyVoted && !rematch.iVoted
            ? `${rematch.oppName} wants a rematch!`
            : `${rematch.oppName} is offline — a rematch starts when you both agree.`}
        </p>
      ) : null}
    </Modal>
  );
}

/** Shown instead of the action bar once the overlay is dismissed. */
export function GameOverBar({ view, game }: { view: RoomView; game: GameView }) {
  const setDismissed = useUiStore((s) => s.setGameOverDismissed);
  const result = game.result;
  if (!result) return null;
  const names: [string, string] = [seatName(view, 0), seatName(view, 1)];
  const you = view.you;
  return (
    <div className="gameover-bar" role="group" aria-label="Game over">
      <p className="gameover-bar__text">
        <strong>{resultTitle(result, you, names)}</strong>
        <span className="tabular"> {resultScoreLine(result, you)}</span>
      </p>
      <div className="gameover-bar__actions">
        <Tooltip content="Show the final scores and highlights again">
          <button type="button" className="btn btn--sm gameover-bar__results" onClick={() => setDismissed(false)}>
            Results
          </button>
        </Tooltip>
        <RematchButton view={view} />
      </div>
    </div>
  );
}
